import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styles from './styles/LotsByGoodsFilteringPanel.module.scss';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../../../../components/checkboxes/checkbox';
import { TextInput } from '../../../../components/controls/inputs';
import { IOption, Select } from '../../../../components/selects/select';
import { LotsByGoodsFilterButtons, LotsByGoodsFilterClearButtons } from './LotsByGoodsFilterButtons';
import GridSelectorInput from '../../../../components/controls/GridSelectorInput';
import { GoodsSelectorModal } from '../../../Dictionaries/Goods';
import { ContractorSelectorModal } from '../../../Dictionaries/Contractor';
import { IDatePeriod } from '../../../../components/datePeriodPicker/datePeriodPicker';
import { DatePeriodPanel } from '../../../../hoc/DatePeriodPanel/DatePeriodPanel';
import { ProducerSelectorModal } from '../../../Dictionaries/Producer';
import { IEntitySimpleDTO } from '../../../../libs/coreapi-dto/@types/common';
import useGridFilter, { GridFilterAction, DefaultLotGridFilter } from '../../../../system/hooks/useGridFilter';
import { DateTime } from 'luxon';
import { DateInput } from '../../../../components/controls/inputs/BaseInput';
import { useComPortScanner } from '../../../../system/hooks/useHubConnection';
import { useTabsContext } from '../../../../system/providers/tabsProvider';
import { SelectControl } from '../../../../components/controls/SelectControl';
import { usePluginContext } from '../../../../system/providers/plugin';
import { ContractorDataProvider } from '../../../../Services/DataProviders/ContractorDataProvider';
import { useAppContext } from '../../../../system/providers/appContextProvider';
import { useLocalStorageChange } from '../../../../system/hooks/useLocalStorageChange';
import { IJournalFilters } from '../../DashboardBusinessControls/interfaces';
import { debounce } from 'lodash';
import { PopoverClue } from '../../../../components/popoverClue/PopoverClue';
import { StoreSelectorPanel } from '../../../../components/storeSelectorPanel';
import { IdTableVariant } from '../../../../@types/enumsGlobal';
import { GoodsGroupAllSelectorModal } from '../../../Dictionaries/GoodsGroup/GoodsGroupAllSelectorModal';
import GridMultipleSelectorInputV2 from '../../../../components/controls/GridMultipleSelectorInputV2';
import StoreSelectorModal from '../../../Dictionaries/Store/StoreSelectorModal';
import SubstanceSelectorModal from '../../../Dictionaries/Substance/SubstanceSelectorModal';
import CureFormSelectorModal from '../../../Dictionaries/CureForm/CureFormSelectorModal';
import GosContractFundingSourceSelectorModal from '../../../Dictionaries/GosContractFundingSource/selector/GosContractFundingSourceSelectorModal';

interface Props {
  dispatchGridFilter: (value: GridFilterAction) => void;
  isHideIcon?: boolean;
  isDefaultStore?: boolean;
  isDashboardPanel?: boolean;
  isLotPanel?: boolean;
  isMandatoryAssortmentPanel?: boolean;
  isFalsificationPanel?: boolean;
  isProAptekaEnabled?: boolean | null;
  includeGoodsGroups?: IEntitySimpleDTO[];
  isTotalQuantity?: boolean
  onIncludeGoodsGroupsChange?: (groups: IEntitySimpleDTO[]) => void;
}

const isNumber = (n) => {
  return typeof n != 'boolean' && !isNaN(n);
};

const isGoodsCode = (n) => {
  return isNumber(n);
};
const isExternalBarcode = (n) => {
  return isNumber(n) && n.length === 13 && n.substring(0, 4) !== '2000';
};
const isInternalBarcode = (n) => {
  return isNumber(n) && n.length === 13 && n.substring(0, 4) === '2000';
};

const MIN_SEARCH_LENGTH = 3;

export const LotsByGoodsFilteringPanel: React.FC<Props> = (props) => {
  const tabsContext = useTabsContext();
  const appContext = useAppContext()
  const { t } = useTranslation();
  const baseT = (value: string) => t('accounting.lotsByGoods.filteringPanel.filterBy.' + value);
  const baseTFilteringPanel = (value: string) => t('accounting.lotsByGoods.filteringPanel.' + value);
  const filterPeriodByT = (value: string) => t('accounting.lotsByGoods.filteringPanel.filterPeriodBy.' + value);
  const filterBalanceT = (value: string) => t('accounting.lotsByGoods.filteringPanel.filterBalanceOffBalance.' + value);
  const filterGnvlsT = (value: string) => t('accounting.lotsByGoods.filteringPanel.filterGnvlsOption.' + value);

  const pluginCtx = usePluginContext();
  const currentIdTab = useRef<string>('');

  const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultLotGridFilter);
  const contractorDataProvider = new ContractorDataProvider(appContext.coreApiService)

  const defaultPeriodOption = { displayName: filterBalanceT('chooseFilter'), value: '', hidden: true } as IOption;
  const defaultSearchOption = { displayName: baseT('goods'), value: 'goodsName' } as IOption;

  const searchFields = [
    baseT('goods'),//"Товар",
    baseT('goodsCode'),//"Код товара",
    baseT('internalBarcode'),//"Внутренний штрихкод",
    baseT('barcodeCode'),//"Заводской штрихкод",
    baseT('registerPrice'),//"Предельная цена",
    baseT('gosContractName'),//"Имя госконтракта",
    baseT('taskProgramName'),//"Имя целевой программы",
    baseT('tradeNameRus'),//"Товарное наименование",
    baseT('groupName'),//"Имя группы товаров",
    baseT('mnn'),//"МНН",
    baseT('retailPriceIncVat'),//"Цена розн. с НДС"   
  ]

  const gnvlsOptions = [
    { displayName: filterGnvlsT('all'), value: '' },
    { displayName: filterGnvlsT('isGnvls'), value: 'true'},
    { displayName: filterGnvlsT('isNotGnvls'), value: 'false' },
  ] as IOption[];

  const [gnvlpOption, setGnvlpOption] = useState<IOption>(gnvlsOptions[0]);

  const seriesBestBeforeOptions = [
    { displayName: filterPeriodByT('seriesBestBefore'), value: '' },
    { displayName: filterPeriodByT('Between'), value: 'Between' },
    { displayName: filterPeriodByT('More'), value: 'More' },
    { displayName: filterPeriodByT('Less'), value: 'Less' },
    { displayName: filterPeriodByT('Eq'), value: 'Eq' }
  ] as IOption[];

  const DefaultDatePeriod = {
    startDate: DateTime.now().plus({ month: -3 }),
    endDate: DateTime.now(),
  } as IDatePeriod;

  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [goods, setGoods] = useState<IEntitySimpleDTO | null>(null);
  const selectedIncludeGoodsGroup = props.includeGoodsGroups ?? [];
  const [store, setStore] = useState<IEntitySimpleDTO | null>(null);  
  const [defaultStore, setDefaultStore] = useState<IEntitySimpleDTO | null>(null);
  const [pharmacy, setPharmacy] = useState<IEntitySimpleDTO | null>(null);
  const [supplier, setSupplier] = useState<IEntitySimpleDTO | null>(null);
  const [producer, setProducer] = useState<IEntitySimpleDTO | null>(null);
  const [substance, setSubstance] = useState<IEntitySimpleDTO | null>(null);
  const [gosContractFundingSource, setGosContractFundingSource] = useState<IEntitySimpleDTO | null>(null);
  const [cureForm, setCureForm] = useState<IEntitySimpleDTO | null>(null);

  const [clearCounter, setClearCounter] = useState(0);
  const [goodsField, setGoodsField] = useState<string>('');
  const [seriesBestBeforeOption, setSeriesBestBeforeOption] = useState<IOption>(seriesBestBeforeOptions[0]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [quantityTotal, setQuantityTotal] = useState<boolean>(props?.isTotalQuantity ?? true);
  const [datePeriodSeriesBestBefore, setDatePeriodSeriesBestBefore] = useState<IDatePeriod>(DefaultDatePeriod);
  const [dateSeriesBestBefore, setDateSeriesBestBefore] = useState<DateTime | undefined | null>(DateTime.now());
  const [savedFilters, setSavedFilters] = useLocalStorageChange<IJournalFilters>('journalFilters');

  const clearLotFilter = () => {
    setPharmacy(null);
    setSupplier(null);
    setGoods(null);
    setStore(null);
    setSubstance(null);
    setGosContractFundingSource(null);
    setCureForm(null);
    setProducer(null);
    setGoodsField('');
    setQuantityTotal(true);
    setSeriesBestBeforeOption(seriesBestBeforeOptions[0])
    setGnvlpOption(gnvlsOptions[0]);
    setClearCounter(prev => prev + 1);
  };

  const addOtherFilters = (): IGridColumnFilter[] => {
    let newColumnFilters: IGridColumnFilter[] = [] as IGridColumnFilter[];

    if (gnvlpOption && gnvlpOption.value !== '') {
      newColumnFilters.push(
        {
          name: 'isGnvls',
          operator: 'Eq',
          value: gnvlpOption.value,
        }
      );
    }
    
    if (quantityTotal) {
      newColumnFilters.push({
        name: 'quantityTotal',
        operator: 'More',
        value: 0,
      });
    }

    if (goods) {
      newColumnFilters.push({
        name: 'idGoodsGlobal',
        operator: 'Eq',
        value: goods.idGlobal,
      });
    }

    if (store) {
      newColumnFilters.push({
        name: 'idStoreGlobal',
        operator: 'Eq',
        value: store.idGlobal,
      });
    }
    if (substance) {
      newColumnFilters.push({
        name: 'idSubstanceGlobal',
        operator: 'Eq',
        value: substance.idGlobal,
      });
    }
    if (gosContractFundingSource) {
      newColumnFilters.push({
        name: 'idGosContractFundingSourceGlobal',
        operator: 'Eq',
        value: gosContractFundingSource.idGlobal,
      });
    }
    if (cureForm) {
      newColumnFilters.push({
        name: 'idCureFormGlobal',
        operator: 'Eq',
        value: cureForm.idGlobal,
      });
    }

    if (pharmacy) {
      newColumnFilters.push({
        name: 'idContractorGlobal',
        operator: 'Eq',
        value: pharmacy.idGlobal,
      });
    }

    if (supplier) {
      newColumnFilters.push({
        name: 'idSupplierGlobal',
        operator: 'Eq',
        value: supplier.idGlobal,
      });
    }

    if (producer) {
      newColumnFilters.push({
        name: 'idProducerGlobal',
        operator: 'Eq',
        value: producer.idGlobal,
      });
    }

    if (seriesBestBeforeOption.value !== seriesBestBeforeOptions[0].value) {
      if (seriesBestBeforeOption.value === seriesBestBeforeOptions[1].value) {
        newColumnFilters.push(
          ...(datePeriodSeriesBestBefore?.startDate
            ? [
              {
                name: "seriesBestBefore",
                value: datePeriodSeriesBestBefore.startDate.toFormat("yyyyMMdd"),
                operator: "MoreOrEq",
              } as IGridColumnFilter,
            ]
            : []),
          ...(datePeriodSeriesBestBefore?.endDate
            ? [
              {
                name: "seriesBestBefore",
                value: datePeriodSeriesBestBefore.endDate.plus({ days: 1 }).toFormat("yyyyMMdd"),
                operator: "LessOrEq",
              } as IGridColumnFilter,
            ]
            : []),
        );
      }
      else {
        newColumnFilters.push({
          name: 'seriesBestBefore',
          operator: seriesBestBeforeOption.value,
          value: dateSeriesBestBefore?.toFormat("yyyyMMdd"),
        });
      }
    }

    return newColumnFilters;
  };

  const addColumnFilter = (searchOptionName: string): void => {
    let newColumnFilters: IGridColumnFilter[] = [] as IGridColumnFilter[];    
    let paramFilter: any;
    let filters = addOtherFilters();
    filters.map((filter) => {
      newColumnFilters.push(filter);
    });

    props.dispatchGridFilter({
      type: 'paramSimpleFilter',
      payload: { gridParamFilter: paramFilter, gridColumnFilter: newColumnFilters },
    });
  };

  const setGoodsFieldColumns = () => {
    if (isExternalBarcode(goodsField)) {
      addColumnFilter('barcodeCode');
    } else if (isInternalBarcode(goodsField)) {
      addColumnFilter('internalBarcode');
    } else if (isGoodsCode(goodsField)) {
      addColumnFilter('goodsCode');
    } else {
      addColumnFilter('goodsName');
    }
  };

  const firstUpdate = useRef(true);
  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    setGoodsFieldColumns();
  }, [goodsField, goods, store, substance, gosContractFundingSource, cureForm, pharmacy, producer, quantityTotal, gnvlpOption, supplier, datePeriodSeriesBestBefore, dateSeriesBestBefore]);

  useEffect(() => {
    if (savedFilters) {
      setStore(null)
      setDefaultStore(null)
      setPharmacy({ displayName: savedFilters.contractorName, idGlobal: savedFilters.contractorId })
    } else {
      props.isDefaultStore && contractorDataProvider.getMe((entity) => {
        if (entity?.idGlobal) {
          setPharmacy({ displayName: entity.name, idGlobal: entity.idGlobal })
          setStore(entity.store)
          setDefaultStore(entity.store)
        }
      })
    }
  }, [savedFilters, props.isDefaultStore])

  useEffect(() => {
    if (tabsContext?.currentTab) {
      currentIdTab.current = tabsContext.currentTab?.id;
    }
  }, []);

  useEffect(() => {
    setDatePeriodSeriesBestBefore(DefaultDatePeriod)
    setDateSeriesBestBefore(DefaultDatePeriod.endDate)
  }, [seriesBestBeforeOption]);

  useEffect(() => {
    if (quantityTotal) {
      pluginCtx.lots.dispatchGridFilter({
        type: "addColumnFilter",
        payload: {
          name: "quantityTotal",
          operator: "More",
          value: 0,
        }
      });
    } else {
      pluginCtx.lots.dispatchGridFilter({
        type: "sortByFilters",
        payload: { gridColumnFilter: gridFilter.columnFilters }
      })
    }
  }, [quantityTotal]);

  useComPortScanner((responseWS) => {
    if (tabsContext.currentTab && (tabsContext.currentTab?.id === currentIdTab.current)) {
      setSearchValue(responseWS)
    }
  })

  const getDateComponent = (): JSX.Element => {
    switch (seriesBestBeforeOption.value) {
      case seriesBestBeforeOptions[0].value:
        return <></>
      case seriesBestBeforeOptions[1].value:
        return <DatePeriodPanel
          datePeriod={datePeriodSeriesBestBefore}
          onDatePeriodChange={(date: IDatePeriod) => setDatePeriodSeriesBestBefore(date)}
        />
      default:
        return <DateInput
          value={dateSeriesBestBefore}
          onBlur={(value) => setDateSeriesBestBefore(value)}
          inline
        />
    }
  }
   
  const searchLotsByGoods = useCallback((value: string) => {
    if (value.length >= MIN_SEARCH_LENGTH) {
      const isPriceSearch = /^\d{1,10}[.,]\d{1,2}$/.test(value);
      
      if (isPriceSearch) {
        // Для цен - точный поиск только по ценовым полям
        const numericValue = value.replace(',', '.'); // заменяем запятую на точку для БД
        const priceConditions = [
          `"registerPrice" = ${numericValue}`,
          `"retailPriceIncVat" = ${numericValue}`
        ];
        const expression = priceConditions.join(' or ');
        props.dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: expression });
      } else {
        const safeValue = value.replace(/%/g, '\\%').replace(/_/g, '\\_');  
        const fields = [
          'goodsName',
          'goodsCode',
          'internalBarcode',
          'barcodeCode',
          'registerPrice',
          'gosContractName',
          'taskProgramName',
          'tradeNameRus',
          'groupName',
          'mnn',
          'retailPriceIncVat',
          'codeKlp'
        ];
        const conditions = fields.map(field => 
          `lower("${field}") like lower('%${safeValue}%')`
        );      
        const expression = conditions.join(' or ');
    
        props.dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: expression });
      }
      
    } else if (value.length === 0) {
      props.dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
    }
  }, [props.dispatchGridFilter]);

  const debouncedSearch = useMemo(
    () => debounce(searchLotsByGoods, 1250),
    [searchLotsByGoods]
  );

  const handleSearchChange = (value: string) => {
    setSearchValue(value);

    if (value.length === 0) {
      debouncedSearch.cancel();
      props.dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
    } else if (value.length >= MIN_SEARCH_LENGTH) {
      debouncedSearch(value);
    }
  };

  const handlePressEnter = () => {
    debouncedSearch.cancel();
    if (searchValue.length >= MIN_SEARCH_LENGTH || searchValue.length === 0) {
      searchLotsByGoods(searchValue);
    }
  };

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  return (
    <>
      <div className={styles.lotsFilteringPanel}>
        <TextInput
          barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
          value={searchValue}
          className={styles.search}
          inputClassName={styles.inlineInput}
          disabled={false}
          placeholder={baseTFilteringPanel('search')}
          inputId={'inputLotsByGoodsSearch'}
          inline={true}
          searchButtonShow={false}
          onChange={(e) => handleSearchChange(e)}
          searchButtonOnClick={handlePressEnter}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handlePressEnter();
            }
          }}
        />
        <div className={styles.popoverSearchInfo}>
          <PopoverClue
            clues={searchFields}
          />
        </div>
        <div style={{marginRight: 15}}>
          <SelectControl
            label={filterGnvlsT('gnvlsOption')}
            inline
            defaultOption={gnvlsOptions[0]}
            value={gnvlpOption}
            onSelect={(option) => setGnvlpOption(option)}
            options={gnvlsOptions}
            style={{boxShadow: "0px 0px 0px 3px #99ccff"}}
          />
        </div>
        <div className={styles.remainsOnlyFilter}>
          <Checkbox
            name="remainsOnlyFilter"
            label= {baseTFilteringPanel('onlyRemains')}
            checked={quantityTotal}
            onChange={() => {
              setQuantityTotal(!quantityTotal);
            }}
            disabled={false}
          />
        </div>
        <div className={styles.remainsOnlyFilter}>
        </div>
        {!props.isHideIcon && <div className={styles.filterButton}>
          <LotsByGoodsFilterButtons
            nameButton={baseTFilteringPanel('nameButtonFilter')}
            isOpen={filterIsOpen}
            onOpenChange={() => setFilterIsOpen(!filterIsOpen)}
          />
        </div>}
      </div>
      <div
        style={{ display: (filterIsOpen || props?.isHideIcon) ? undefined : 'none' }}
      >
        {props.isDashboardPanel ? (
          <div>
            <div className={styles.lotsFilteringTwoDashboard}>
              <div className={styles.additionalFilter}>
                <GridSelectorInput
                  className={styles.width100}
                  id="idGoodsGlobal"
                  placeholder={baseTFilteringPanel('goods')}
                  selectorModalJsx={GoodsSelectorModal}
                  selectedEntity={goods}
                  onClear={() => {
                    setGoods(null);
                  }}
                  onSelect={(entity) => {
                    setGoods(entity);
                  }}
                />
              </div>
              <div className={styles.additionalFilter}>
                <GridMultipleSelectorInputV2
                  className={styles.width100}
                  placeholder={baseT('goodsGroup')}
                  selectedEntity={selectedIncludeGoodsGroup?.[0]}
                  selectedEntities={selectedIncludeGoodsGroup}
                  id={`idGoodsGroup`}
                  switchOnchange
                  gridFilter={gridFilter}
                  selectorModalJsx={GoodsGroupAllSelectorModal}
                  onSelect={(entities: IEntitySimpleDTO[]) => {
                    props.onIncludeGoodsGroupsChange?.(entities);
                  }}
                  onClear={() => props.onIncludeGoodsGroupsChange?.([])}
                  isSearchForMultyselect
                  dataFromPluginGridSelectorInput={selectedIncludeGoodsGroup}
                  isProAptekaEnabled={props.isProAptekaEnabled}
                />
              </div>
            </div>
            <div className={styles.lotsFilteringTwoDashboard}>
              <div className={styles.doubleFilterWrapper}>
                <GridSelectorInput
                  className={styles.width100}
                  id="idStoreGlobal"
                  placeholder={baseTFilteringPanel('store')}
                  selectorModalJsx={StoreSelectorModal}
                  selectedEntity={store}
                  hiddenClearButton={defaultStore !== null}
                  onClear={() => {
                    setStore(null);
                  }}
                  onSelect={(entity) => {
                    setStore(entity);
                  }}
                />
                <GridSelectorInput
                  className={styles.width100}
                  id="idContractorGlobal"
                  placeholder={baseTFilteringPanel('pharmacy')}
                  selectorModalJsx={ContractorSelectorModal}
                  treeViewCheckDirectoryType={'pharmacy'}
                  selectedEntity={pharmacy}
                  onClear={() => {
                    setPharmacy(null);
                  }}
                  onSelect={(entity) => {
                    setPharmacy(entity);
                  }}
                />
              </div>
              <div className={styles.doubleFilterWrapper}>
                <div className={styles.providerFilter}>
                  <GridSelectorInput
                    className={styles.width100}
                    id="idProducerGlobal"
                    placeholder={baseTFilteringPanel('manufacturer')}
                    selectorModalJsx={ProducerSelectorModal}
                    selectedEntity={producer}
                    onClear={() => {
                      setProducer(null);
                    }}
                    onSelect={(entity) => {
                      setProducer(entity);
                    }}
                  />
                </div>
                <div className={styles.pharmacyFilter}>
                  <GridSelectorInput
                    className={styles.width100}
                    id="idSupplierGlobal"
                    placeholder={baseTFilteringPanel('supplier')}
                    selectorModalJsx={ContractorSelectorModal}
                    selectedEntity={supplier}
                    onClear={() => {
                      setSupplier(null);
                    }}
                    onSelect={(entity) => {
                      setSupplier(entity);
                    }}
                  />
                </div>
              </div>
              {props.isMandatoryAssortmentPanel && (
                <div className={styles.substanceAndCureFormFilter}>
                  <GridSelectorInput
                    className={styles.width100}
                    id="idSubstanceGlobal"
                    placeholder={baseTFilteringPanel('substance')}
                    selectorModalJsx={SubstanceSelectorModal}
                    selectedEntity={substance}
                    onClear={() => {
                      setSubstance(null);
                    }}
                    onSelect={(entity) => {
                      setSubstance(entity);
                    }}
                  />
                  <GridSelectorInput
                    className={styles.width100}
                    id="idCureFormGlobal"
                    placeholder={baseTFilteringPanel('cureForm')}
                    selectorModalJsx={CureFormSelectorModal}
                    selectedEntity={cureForm}
                    onClear={() => {
                      setCureForm(null);
                    }}
                    onSelect={(entity) => {
                      setCureForm(entity);
                    }}
                  />
                </div>
              )}
              {props.isFalsificationPanel && (
                <div className={styles.gosContractFundingSourceFilter}>
                  <GridSelectorInput
                    className={styles.width100}
                    id="idGosContractFundingSourceGlobal"
                    placeholder={baseTFilteringPanel('gosContractFundingSource')}
                    selectorModalJsx={GosContractFundingSourceSelectorModal}
                    selectedEntity={gosContractFundingSource}
                    onClear={() => {
                      setGosContractFundingSource(null);
                    }}
                    onSelect={(entity) => {
                      setGosContractFundingSource(entity);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className={styles.lotsFilteringTwo}>
              <div className={styles.additionalFilter}>
                <GridSelectorInput
                  className={styles.width100}
                  id="idGoodsGlobal"
                  placeholder={baseTFilteringPanel('goods')}
                  selectorModalJsx={GoodsSelectorModal}
                  selectedEntity={goods}
                  onClear={() => {
                    setGoods(null);
                  }}
                  onSelect={(entity) => {
                    setGoods(entity);
                  }}
                />
              </div>
                {props.isLotPanel ? (
                  <div className={styles.pharmacyAndStoreFilter}>
                    <StoreSelectorPanel
                      resetKey={clearCounter}
                      offUserFilter
                      we={props.isDefaultStore}
                      contractor={{
                        label: t(""),
                        className: styles.gridSelInputCustom,
                        placeholder: 'Аптека',
                        value: pharmacy,
                        onSelect: (value) => setPharmacy(value),
                        onClear: () => setPharmacy(null),
                        treeViewCheckDirectoryType: 'pharmacy',
                      }}
                      store={{
                        label: t(""),
                        style: { width: '50%' },
                        placeholder: 'Склад',
                        value: store,
                        onSelect: (value) => setStore(value),
                        onClear: () => {
                          setStore(null);
                        },
                      }}
                    />
                  </div>
                ) : (
                    <div className={styles.doubleFilterWrapper_40}>
                      <GridSelectorInput
                        className={styles.width100}
                        id="idStoreGlobal"
                        placeholder={baseTFilteringPanel('store')}
                        selectorModalJsx={StoreSelectorModal}
                        selectedEntity={store}
                        hiddenClearButton={defaultStore !== null}
                        onClear={() => {
                          setStore(null);
                        }}
                        onSelect={(entity) => {
                          setStore(entity);
                        }}
                      />
                      <GridSelectorInput
                        className={styles.width100}
                        id="idContractorGlobal"
                        placeholder={baseTFilteringPanel('pharmacy')}
                        selectorModalJsx={ContractorSelectorModal}
                        treeViewCheckDirectoryType={'pharmacy'}
                        selectedEntity={pharmacy}
                        onClear={() => {
                          setPharmacy(null);
                        }}
                        onSelect={(entity) => {
                          setPharmacy(entity);
                        }}
                      />
                    </div>
                )}
              <div className={styles.providerFilter}>
                <GridSelectorInput
                  className={styles.width100}
                  id="idProducerGlobal"
                  placeholder={baseTFilteringPanel('manufacturer')}
                  selectorModalJsx={ProducerSelectorModal}
                  selectedEntity={producer}
                  onClear={() => {
                    setProducer(null);
                  }}
                  onSelect={(entity) => {
                    setProducer(entity);
                  }}
                />
              </div>
              <div className={styles.pharmacyFilter}>
                <GridSelectorInput
                  className={styles.width100}
                  id="idSupplierGlobal"
                  placeholder={baseTFilteringPanel('supplier')}
                  selectorModalJsx={ContractorSelectorModal}
                  selectedEntity={supplier}
                  onClear={() => {
                    setSupplier(null);
                  }}
                  onSelect={(entity) => {
                    setSupplier(entity);
                  }}
                />
              </div>
            </div>
            {props.isMandatoryAssortmentPanel && (
              <div className={styles.substanceAndCureFormFilter}>
                <GridSelectorInput
                  className={styles.width100}
                  id="idSubstanceGlobal"
                  placeholder={baseTFilteringPanel('substance')}
                  selectorModalJsx={SubstanceSelectorModal}
                  selectedEntity={substance}
                  onClear={() => {
                    setSubstance(null);
                  }}
                  onSelect={(entity) => {
                    setSubstance(entity);
                  }}
                />
                <GridSelectorInput
                  className={styles.width100}
                  id="idCureFormGlobal"
                  placeholder={baseTFilteringPanel('cureForm')}
                  selectorModalJsx={CureFormSelectorModal}
                  selectedEntity={cureForm}
                  onClear={() => {
                    setCureForm(null);
                  }}
                  onSelect={(entity) => {
                    setCureForm(entity);
                  }}
                />
              </div>
            )}
            {props.isFalsificationPanel && (
              <div className={styles.gosContractFundingSourceFilter}>
                <GridSelectorInput
                  className={styles.width100}
                  id="idGosContractFundingSourceGlobal"
                  placeholder={baseTFilteringPanel('gosContractFundingSource')}
                  selectorModalJsx={GosContractFundingSourceSelectorModal}
                  selectedEntity={gosContractFundingSource}
                  onClear={() => {
                    setGosContractFundingSource(null);
                  }}
                  onSelect={(entity) => {
                    setGosContractFundingSource(entity);
                  }}
                />
              </div>
            )}
          </div>
        )}
        {!props.isHideIcon &&
          <div className={styles.lotsFilteringThree}>
            <div className={styles.lotsFilteringThreeWrapper}>
              <div className={styles.periodByFilter}>
                <SelectControl
                  // label={filterPeriodByT('seriesBestBefore')}
                  defaultOption={seriesBestBeforeOptions[0]}
                  value={seriesBestBeforeOption}
                  onSelect={(option) => setSeriesBestBeforeOption(option)}
                  options={seriesBestBeforeOptions}
                />
              </div>
              <div className={styles.datePeriodFilter}>
                {getDateComponent()}
              </div>
              <div className={styles.balanceOffBalanceFilter}>
                <Select
                  disabled={true}
                  defaultOption={defaultPeriodOption}
                  onSelect={(option) => {
                    //props.setFilters({ ...props.filters, balanceOffBalance: option })
                    //props.setLotParamFilter({ ...props.lotParamFilter, balance: option.value })
                    //props.setFilter && props.setFilter({ ...props.filter, balance: option.value } as Filter)
                  }}
                  options={[
                    defaultPeriodOption,
                    { displayName: filterBalanceT('balance'), value: 'balance' },
                    { displayName: filterBalanceT('offBalance'), value: 'offBalance' },
                  ]}
                ></Select>
              </div>
              <div className={styles.IZFilter}>
                <Checkbox
                  name="izFilter"
                  label="ИЗ"
                  onChange={(checked) => {
                    //props.setFilters({ ...props.filters, iz: checked })
                  }}
                  disabled={true}
                />
              </div>
              <div className={styles.OAFilter}>
                <Checkbox
                  name="oaFilter"
                  label="ОА"
                  onChange={(checked) => {
                    //props.setFilters({ ...props.filters, oa: checked })
                  }}
                  disabled={true}
                />
              </div>
            </div>
            {!props.isHideIcon && <div className={styles.filterButton}>
              <LotsByGoodsFilterClearButtons
                nameButton={''}
                isOpen={filterIsOpen}
                onClick={() => {
                  clearLotFilter();
                }}
              />
            </div>}              
          </div>}
      </div>
    </>
  );
};
